import { Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { ISubj } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import TranslateText from 'components/hoc/TranslateText';

import { IcisStaty } from 'services/cisStaty';

import { ETypOsoby } from 'enums/amlz/typOsoby';

import typOsobySelect from 'data/codebooks/amlz/typOsobySelect';

import icoValidation from 'utils/validations/icoValidation';
import procentaMinimum10Validation from 'utils/validations/procentaMinimum10Validation';
import rodneCisloValidation from 'utils/validations/rodneCisloValidation';

interface IC3PridatOsobu {
    personData: ISubj | undefined;
    setPersonData: (value: ISubj | undefined) => void;
    setSubmitPersonData: (value: ISubj | null) => void;
    countries?: IcisStaty[];
    mode: 'C3A' | 'C3B';
}

const C3PridatOsobu: React.FC<IC3PridatOsobu> = ({
    personData,
    setSubmitPersonData,
    setPersonData,
    countries,
    mode
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(true);
    const [updatedField, setUpdatedField] = useState<string>('');

    const handleSelectChange = (name: string, value: string[] | string) => {
        if (typeof value === 'string') {
            form.setFieldsValue({
                [name]: value
            });
        } else {
            form.setFieldsValue({
                [name]: [...value]
            });
        }
        setUpdatedField(name);
    };

    useEffect(() => {
        // validacia pri zmene selectu 'CZ' validuje rodne cislo, inak datum nar.
        if (updatedField === 'Pzemepuv') form.validateFields(['Pidmb']);
        setUpdatedField('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedField]);

    useEffect(() => {
        // nastavenie selectu pri nacitani
        form.setFieldsValue({
            tpma: personData?.tpma
        });
        personData?.tpma === ETypOsoby.FYZICKAOSOBA &&
            form.setFieldsValue({
                Fzemepuv: personData?.zemepuv
            });
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        // format date value before submit. /////// fyzosoba F pravosoba P
        const values = fieldsValue.Fdatumnar
            ? {
                  ...fieldsValue,
                  Fdatumnar: fieldsValue.Fdatumnar.format('YYYY-MM-DD')
              }
            : {
                  ...fieldsValue
              };

        const pravnickaOsoba = {
            idmb: values.Pidmb,
            jmeno: values.Pjmeno,
            podil: values.Ppodil,
            zemepuv: typeof values.Pzemepuv === 'object' ? values.Pzemepuv : [values.Pzemepuv],
            tpma: values.tpma
        };
        const fyzickaOsoba = {
            datumnar: values.Fdatumnar,
            idmb: values.Fidmb,
            jmeno: values.Fjmeno,
            misto: values.Fmisto,
            podil: values.Fpodil,
            prijmeni: values.Fprijmeni,
            zemepuv: values.Fzemepuv,
            tpma: values.tpma
        };
        setSubmitPersonData(values.tpma === ETypOsoby.FYZICKAOSOBA ? fyzickaOsoba : pravnickaOsoba);
    };

    return (
        <Row justify="start">
            {!loading && (
                <Form
                    preserve={false}
                    scrollToFirstError={true}
                    form={form}
                    layout="vertical"
                    initialValues={{
                        remember: true
                    }}
                    onFinish={onFinish}
                    name="C3PridatOsobu"
                    style={{
                        width: '100%'
                    }}
                >
                    <p>
                        <TranslateText>
                            {mode === 'C3B' ? 'FORMULARE_AMLZ_C3B_TEXT' : 'FORMULARE_AMLZ_C3A_PODILNIKTEXT'}
                        </TranslateText>
                    </p>
                    <Title level={4} text={`FORMULARE_AMLZ_${mode}_PODILNIK`} />
                    <SelectComponent
                        isFormItem={true}
                        name="tpma"
                        labelParagraph="FORMULARE_AMLZ_C3_PODILNIK_JE"
                        defaultValue={personData?.tpma}
                        onChange={value => handleSelectChange('tpma', value)}
                        data={typOsobySelect}
                    />
                    {form.getFieldsValue(['tpma']).tpma === ETypOsoby.FYZICKAOSOBA ? (
                        <>
                            <InputComponent
                                label="JMENO"
                                name="Fjmeno"
                                initialValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.jmeno : ''}
                                type="text"
                                maxLength={100}
                            />
                            <InputComponent
                                label="PRIJMENI"
                                name="Fprijmeni"
                                initialValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.prijmeni : ''}
                                type="text"
                                maxLength={100}
                            />
                            <SelectComponent
                                isFormItem={true}
                                name="Fzemepuv"
                                labelParagraph="FORMULARE_ZEME_PUVODU"
                                mode="multiple"
                                defaultValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.zemepuv : []}
                                onChange={value => handleSelectChange('Fzemepuv', value)}
                                data={countries ? countries : []}
                            />
                            <InputComponent
                                label="FORMULARE_MISTO_NAROZENI"
                                name="Fmisto"
                                initialValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.misto : ''}
                                type="text"
                                maxLength={100}
                                notRequired={true}
                            />
                            {form.getFieldsValue(['Fzemepuv']).Fzemepuv !== undefined &&
                            form.getFieldsValue(['Fzemepuv']).Fzemepuv.length === 1 &&
                            form.getFieldsValue(['Fzemepuv']).Fzemepuv.includes('CZ') ? (
                                <InputComponent
                                    label="FORMULARE_RODNE_CISLO_CR"
                                    name="Fidmb"
                                    initialValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.idmb : ''}
                                    type="text"
                                    maxLength={10}
                                    rules={rodneCisloValidation()}
                                />
                            ) : (
                                <DatePickerComponent
                                    name="Fdatumnar"
                                    label="FORMULARE_DATUM_NAROZENI"
                                    initialValue={personData?.datumnar ? dayjs(personData?.datumnar) : undefined}
                                    disabledDate={current => current > dayjs()}
                                    defaultPickerValue={dayjs(personData?.datumnar)}
                                    defaultValue={personData?.datumnar ? dayjs(personData?.datumnar) : undefined}
                                />
                            )}
                            <InputNumberComponent
                                name="Fpodil"
                                label="FORMULARE_AMLZ_C3A_PODIL"
                                maxLength={16}
                                precision={2}
                                rules={procentaMinimum10Validation()}
                                initialValue={personData?.tpma === ETypOsoby.FYZICKAOSOBA ? personData.podil : ''}
                            />
                        </>
                    ) : (
                        <>
                            <InputComponent
                                label="EMP_PODANE_ZADOSTI_NAZEV_SPOLECNOSTI"
                                name="Pjmeno"
                                initialValue={personData?.jmeno}
                                type="text"
                                maxLength={100}
                            />
                            <InputComponent
                                label="ICO"
                                name="Pidmb"
                                initialValue={personData?.idmb}
                                type="text"
                                rules={icoValidation('Pzemepuv')}
                                maxLength={form.getFieldValue('Pzemepuv') === 'CZ' ? 10 : 50}
                            />
                            <SelectComponent
                                name="Pzemepuv"
                                isFormItem={true}
                                labelParagraph="FORMULARE_ZEME_SIDLA"
                                defaultValue={personData?.zemepuv}
                                onChange={value => {
                                    handleSelectChange('Pzemepuv', value);
                                    form.validateFields(['Pidmb']);
                                }}
                                data={countries ? countries : []}
                            />
                            <InputNumberComponent
                                name="Ppodil"
                                label="FORMULARE_AMLZ_C3A_PODIL"
                                maxLength={16}
                                precision={2}
                                rules={procentaMinimum10Validation()}
                                initialValue={personData?.podil}
                            />
                        </>
                    )}
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            id="c3ZpetBtn"
                            type="default"
                            onClick={() => {
                                setSubmitPersonData(null);
                                setPersonData(undefined);
                            }}
                        />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="c3PokracovatBtn"
                            type="primary"
                            onClick={() => form.submit()}
                        />
                    </Row>
                </Form>
            )}
        </Row>
    );
};

export default C3PridatOsobu;
